import { store } from '@/ui/store';
import { Logo } from '@/ui/Logo';
import { Icon } from '@/ui/icons';
import { canFilterSheet, sheetFilterActive } from '@/model/sheetFilter';
import { buildTitle, versionLabel } from '@/version';
import { useStore } from '@/ui/useStore';
import { useState } from 'react';
import { FORMATS } from '@/engine/format';
import { getCell } from '@/model/workbook';
import type { FormatId } from '@/model/types';
import type { SaveStatus } from '@/giraffe/persist';
import { agoLabel, saveAffordance } from '@/model/persistFormat';
import { FileMenu } from '@/ui/FileMenu';

interface Props {
  onOpenData: () => void;
  onInsertChart: () => void;
  onOpenHelp: () => void;
  /** Undefined when there is no project to save into. */
  onSave?: () => Promise<void>;
  save?: SaveStatus;
}

export function Toolbar({ onOpenData, onInsertChart, onOpenHelp, onSave, save }: Props): JSX.Element {
  useStore();
  const sheet = store.sheet();
  const activeFmt = getCell(sheet, store.sel.row, store.sel.col)?.fmt ?? 'general';

  return (
    <div className="gt-toolbar">
      <Logo />
      {/* Beside the mark, where a version belongs. The commit and the build
          time are on the hover, for when "which one is this" needs an answer
          that nobody had to remember to update. */}
      <span className="gt-version" title={buildTitle()}>
        {versionLabel()}
      </span>
      <div className="gt-tb-sep" />

      <FileMenu />

      <div className="gt-tb-sep" />

      <div className="gt-tb-group">
        <button
          className={`gt-btn${store.selectionHas('bold') ? ' is-on' : ''}`}
          onClick={() => store.applyStyle({ bold: !store.selectionHas('bold') })}
          title="Bold (Cmd/Ctrl+B)"
          style={{ fontWeight: 700 }}
        >
          B
        </button>
        <button
          className={`gt-btn${store.selectionHas('italic') ? ' is-on' : ''}`}
          onClick={() => store.applyStyle({ italic: !store.selectionHas('italic') })}
          title="Italic"
          style={{ fontStyle: 'italic' }}
        >
          I
        </button>
        <button className="gt-btn" onClick={() => store.applyStyle({ align: 'left' })} title="Align left"><Icon.alignLeft /></button>
        <button className="gt-btn" onClick={() => store.applyStyle({ align: 'center' })} title="Align centre"><Icon.alignCenter /></button>
        <button className="gt-btn" onClick={() => store.applyStyle({ align: 'right' })} title="Align right"><Icon.alignRight /></button>
      </div>

      <div className="gt-tb-sep" />

      <select
        className="gt-select"
        value={activeFmt}
        onChange={(e) => store.applyFormat(e.target.value as FormatId)}
        title="Number format"
      >
        {FORMATS.map((f) => (
          <option key={f.id} value={f.id}>
            {f.label}
          </option>
        ))}
      </select>

      {store.devMode ? (
        <>
          <div className="gt-tb-sep" />
          <UnitsSelect />
        </>
      ) : null}

      <div className="gt-tb-sep" />

      <div className="gt-tb-group">
        <button className="gt-btn" onClick={() => store.fillDown()} title="Fill down (Cmd/Ctrl+D)">↓ Fill</button>
        <button className="gt-btn" onClick={() => store.fillRight()} title="Fill right (Cmd/Ctrl+R)">→ Fill</button>
      </div>

      <div className="gt-tb-sep" />

      <button className="gt-btn gt-btn-primary gt-btn-icon" onClick={onOpenData} title="Insert a schedule of Giraffe data as formulas">
        <Icon.schedule />
        Insert schedule
      </button>
      {store.devMode ? (
        <button
          className={`gt-btn gt-btn-icon${store.dbWizard ? ' is-on' : ''}`}
          onClick={() => (store.dbWizard ? store.closeDbWizard() : store.openDbWizard())}
          title="Insert a database: pick objects by criteria (layer, usage, anything), then the columns to list"
        >
          <Icon.database />
          Database
        </button>
      ) : null}
      <button
        className={`gt-btn gt-btn-icon${sheet.sheetFilter ? ' is-on' : ''}`}
        disabled={!canFilterSheet(sheet)}
        onClick={() => store.toggleFilterBar()}
        title={
          !canFilterSheet(sheet)
            ? 'The project sheets show everything the project has'
            : sheet.sheetFilter
              ? 'Take the filter off this sheet'
              : sheet.role === 'areas' || sheet.role === 'features'
                ? "Filter this sheet: hides rows that don't match, and every GI formula and database on it applies what's picked too — layer, usage, anything"
                : "Filter this sheet: every GI formula and database on it applies what's picked — layer, usage, anything"
        }
      >
        <Icon.filter />
        Filter
        {sheetFilterActive(sheet) ? <span className="gt-filter-dot" aria-label="This sheet is filtered" /> : null}
      </button>
      <button className="gt-btn" onClick={onInsertChart} title="Insert a chart from the selected range">
        Chart
      </button>

      {/* Notes from things done on an ordinary sheet — "Selected 3 on the map"
          from a database row — which the role sheets show beside their own
          buttons. */}
      {!sheet.role ? <WriteStatus /> : null}
      <UsagePush />
      <FeaturePush />
      <BlockPush />

      <div className="gt-tb-spacer" />

      {onSave && save ? <SaveButton save={save} onSave={onSave} /> : null}
      <button className="gt-btn gt-btn-primary" onClick={onOpenHelp} title="Functions and shortcuts">
        Help
      </button>
    </div>
  );
}

/**
 * Metric or imperial.
 *
 * Three settings, not two: the default FOLLOWS the project, so a sheet made in
 * a project set to feet reads in feet without anyone choosing anything, and one
 * whose project setting later changes moves with it. The other two pin it.
 *
 * The tooltip says what the project actually asked for — including a spelling
 * this app did not recognise, which is the one case where the default is doing
 * something the user can see is wrong.
 */
function UnitsSelect(): JSX.Element {
  const override = store.unitOverride();
  const project = store.projectSystem();
  // Money rates are never converted (data/units.ts): they stay in the project's
  // units. Pinned to the other system, an area times a rate mixes m² with $/ft²
  // — so the mismatch is shouted, not tucked into a tooltip.
  const mismatch = override !== undefined && override !== project;
  return (
    <>
    {mismatch && (
      <span
        className="gt-units-warn"
        role="img"
        aria-label="Units differ from the project"
        title={`This sheet shows ${override}, but the project is ${project}.\nCosts and prices are NOT converted — they are still per ${project === 'imperial' ? 'ft²' : 'm²'}, so area × rate will be wrong.\nSet Units back to "project" to fix.`}
      >
        !
      </span>
    )}
    <select
      className="gt-select"
      value={override ?? 'auto'}
      onChange={(e) => {
        const v = e.target.value;
        store.setUnitOverride(v === 'auto' ? undefined : (v as 'metric' | 'imperial'));
      }}
      title={`Units for every project number on the sheet — areas and lengths. Costs and prices are never converted.\nThe project asks for: ${store.projectUnitsLabel()}\nGiraffe's own numbers are metric; this is how they are shown.`}
    >
      <option value="auto">Units: project ({project === 'imperial' ? 'ft' : 'm'})</option>
      <option value="metric">Units: metric (m, m²)</option>
      <option value="imperial">Units: imperial (ft, ft²)</option>
    </select>
    </>
  );
}

/**
 * Push on the Usages sheet. It appears only there, and only when the sheet says
 * something the project does not — a button that is always visible and usually
 * does nothing teaches people to ignore it.
 */
function UsagePush(): JSX.Element | null {
  const [busy, setBusy] = useState(false);
  /** Push carries deletions only when this is armed — see the button beside it. */
  const [armedDelete, setArmedDelete] = useState(false);

  if (store.sheet().role !== 'usages') return null;
  const status = store.writeStatus;
  const diff = store.usageDiff();
  const count = diff.changes.length + diff.removed.length + diff.added.length;
  if (count === 0) {
    // `store.pushUsages` already put its result in writeStatus, and this kept a
    // second copy of it in local state — so the store's faded on its timer and
    // the copy stayed on screen forever, which is the version people saw.
    const message = status?.detail ?? null;
    if (!message) return null;
    const failed = status?.ok === false;
    return (
      <span key={message} className={`gt-chip is-transient${failed ? ' is-error' : ''}`} title={message}>
        {failed ? '⚠ ' : ''}
        {message.length > 90 ? `${message.slice(0, 90)}…` : message}
      </span>
    );
  }

  // A cleared name is the only way a deletion reaches Push — the menu deletes a
  // usage directly. It still has to be confirmed, which is what arming is for.
  const push = (withDelete: boolean): void => {
    setBusy(true);
    // Clear the previous note now rather than leaving it under a spinner.
    store.setWriteStatus(null);
    void store.pushUsages(() => withDelete).then(() => {
      setBusy(false);
      setArmedDelete(false);
    });
  };

  return (
    <>
      <button
        className="gt-btn gt-btn-primary"
        disabled={busy}
        title={describe(diff)}
        onClick={() => push(armedDelete)}
      >
        {busy ? 'Pushing…' : `Push ${count}`}
      </button>
      {diff.removed.length > 0 ? (
        <button
          className={armedDelete ? 'gt-confirm' : 'gt-btn gt-btn-danger'}
          disabled={busy}
          title={`Also remove ${diff.removed.join(', ')} from the project`}
          // The confirm IS the push. Arming and then having to find the other
          // button reads as a dead button: you say yes and nothing happens.
          onClick={() => (armedDelete ? push(true) : setArmedDelete(true))}
        >
          {armedDelete
            ? `Yes, delete ${diff.removed.length}`
            : `Delete ${diff.removed.length} usage${diff.removed.length === 1 ? '' : 's'}?`}
        </button>
      ) : null}
      <button className="gt-btn" disabled={busy} onClick={() => store.reloadUsages()} title="Discard edits and re-read the project">
        Discard
      </button>
    </>
  );
}

function describe(diff: ReturnType<typeof store.usageDiff>): string {
  const renamedTo = new Set(diff.renamed.map((r) => r.to.toLowerCase()));
  const lines = diff.renamed.map((r) => `Rename: ${r.from} → ${r.to}`);
  lines.push(...diff.added.filter((a) => !renamedTo.has(a.name.toLowerCase())).map((a) => `New usage: ${a.name}`));
  lines.push(...diff.changes.slice(0, 8).map((c) => `${c.name}: ${c.field} → ${String(c.to)}`));
  if (diff.changes.length > 8) lines.push(`…and ${diff.changes.length - 8} more`);
  if (diff.removed.length > 0) lines.push(`Delete: ${diff.removed.join(', ')}`);
  return lines.join('\n');
}


/**
 * The Features and Areas sheets' map controls.
 *
 * Show/hide is applied rather than pushed — it drives the MAP, not the project,
 * and it is separate from the property edits for that reason: switching a
 * building off to look behind it should not be entangled with writing a rent
 * onto it. Show all and the push retry are Features-only: Areas has no
 * property edits of its own to push, and the Show toggle it shares with
 * Features is still driven from there. "Select on map" works from either
 * sheet — `rowIdsForSheet` already resolves a row label back to a feature id
 * the same way on both.
 */
function FeaturePush(): JSX.Element | null {
  const role = store.sheet().role;
  if (role !== 'features' && role !== 'areas') return null;
  const hidden = store.hiddenFeatures.size;

  return (
    <>
      {/* No Apply: the Show column IS the control, and a button beside it
          would only ask for the same thing to be said twice. */}
      {role === 'features' && hidden > 0 ? (
        <button className="gt-btn" onClick={() => store.showAllFeatures()} title="Switch every feature back on">
          Show all ({hidden})
        </button>
      ) : null}
      <button
        className="gt-btn"
        title="Select the features these rows name, on the map"
        onClick={() => void store.selectFeaturesFromRows()}
      >
        Select on map
      </button>
      {/* No Push: an edited property goes to the feature on its own. What is
          left is the part that can fail, and a failure with no way back is
          worse than a button — so the retry appears exactly when it is needed.
          Areas pushes nothing of its own, so it gets no retry button — but it
          still shows the SAME "Selected N on the map" confirmation Features
          gets, since `selectFeaturesFromRows` sets `writeStatus` the same way
          regardless of which sheet called it. */}
      <WriteStatus onRetry={role === 'features' ? () => void store.pushFeatures() : undefined} />
    </>
  );
}

/**
 * The Blocks sheet has no button, because there is nothing to press.
 *
 * A value typed onto a block goes to the definition on its own — the column
 * says what the property is called, the row says which block, the cell says the
 * value, and there is no decision left for a button to carry. What remains is
 * the part that can fail: a write the host refused is invisible otherwise.
 */
function BlockPush(): JSX.Element | null {
  if (store.sheet().role !== 'blocks') return null;
  return <WriteStatus onRetry={() => void store.pushBlocks()} />;
}

/**
 * What the last write said, and a way back from a failure.
 *
 * These sheets write themselves, so there is no button to press again — and a
 * refusal with nothing to press is a sheet the user cannot get out of sync
 * with the project. The retry appears only when one has failed.
 */
function WriteStatus({ onRetry }: { onRetry?: () => void }): JSX.Element | null {
  const status = store.writeStatus;
  if (!status) return null;
  return (
    <>
      <span key={status.detail} className={`gt-chip is-transient${status.ok ? '' : ' is-error'}`} title={status.detail}>
        {status.ok ? '' : '⚠ '}
        {status.detail.length > 60 ? `${status.detail.slice(0, 60)}…` : status.detail}
      </span>
      {status.ok || !onRetry ? null : (
        <button className="gt-btn" onClick={onRetry} title="Send it again">
          Retry
        </button>
      )}
    </>
  );
}


/**
 * The save button IS the state.
 *
 * Borrowed from `~/Documents/code/paper`, which gets the important part right:
 * one control that says which of the three things is true rather than a button
 * beside a status line saying something else. "Save" is offered only when there
 * is something to save, so a disabled "Saved ✓" is a fact rather than a dead
 * control, and a dot marks unsaved work for anyone reading the shape of the
 * toolbar rather than its words.
 *
 * The tooltip carries how long ago the last one landed, because "saved" with no
 * time attached is the claim people most want to check.
 */
function SaveButton({ save, onSave }: { save: SaveStatus; onSave: () => Promise<void> }): JSX.Element {
  const { label, busy, dirty } = saveAffordance(save);

  return (
    <span className="gt-save">
      <button
        className={`gt-btn${dirty && !busy ? ' gt-btn-primary' : ''}`}
        onClick={() => void onSave()}
        disabled={busy || !dirty}
        title={`${save.detail}\nLast saved to the project: ${agoLabel(save.lastSavedAt)}`}
      >
        {label}
      </button>
      {dirty && !busy ? <span className="gt-save-dot" aria-label="Unsaved changes" /> : null}
    </span>
  );
}
